import type { RunArtifact } from '@app/shared';
import { Button, Cluster, CodeBlock, Icon, Icons, Link, StatusBadge, Stack, Text } from '../ui';
import { Markdown } from './Markdown';
import { downloadTextFile, printReportAsPdf } from '../lib/reportExport';

/**
 * Run outputs (§6.5) — the FIRST, most prominent thing on the run page (issue #120).
 * For a research run this is the report rendered from Markdown with MD/PDF downloads
 * front-and-centre (§13.1, WP-R5); for an inbox run it is the draft/artifact. Each
 * web-citing output shows the `sources` it cited (§13). When no output exists yet the
 * run page shows an in-progress state instead of rendering this component.
 */

const ARTIFACT_KIND_LABEL: Record<RunArtifact['kind'], string> = {
  draft_email: 'Draft email',
  report: 'Report',
  file: 'File',
  structured: 'Structured output',
};

export function RunOutputs({ artifacts }: { artifacts: RunArtifact[] }) {
  return (
    <Stack gap="6">
      {artifacts.map((artifact) =>
        artifact.kind === 'report' && typeof artifact.body.text === 'string' ? (
          <ReportArtifact key={artifact.id} artifact={artifact} markdown={artifact.body.text} />
        ) : (
          <Stack key={artifact.id} gap="2" data-testid="artifact" data-kind={artifact.kind}>
            <Cluster gap="2">
              <StatusBadge tone="neutral" size="sm" label={ARTIFACT_KIND_LABEL[artifact.kind]} />
              <Text weight="semibold">{artifact.title}</Text>
            </Cluster>
            {typeof artifact.body.text === 'string' ? (
              <Text size="md" style={{ whiteSpace: 'pre-wrap' }}>
                {artifact.body.text}
              </Text>
            ) : (
              <CodeBlock value={(artifact.body.data ?? artifact.body) as unknown} />
            )}
            {artifact.sources && artifact.sources.length > 0 && <Sources sources={artifact.sources} />}
          </Stack>
        ),
      )}
    </Stack>
  );
}

/** Slugify a report title into a safe download filename base. */
function slugify(title: string): string {
  const base = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
  return base || 'research-report';
}

/**
 * A finalised research report (§13.1, WP-R5): the Markdown body rendered richly, its
 * web citations, and self-contained Markdown/PDF downloads. The Markdown already
 * carries the full report (title, executive summary, findings, and a Sources section),
 * so the raw `.md` download is a faithful copy and the PDF is a print-styled render of
 * the same document (no external CDN/network).
 */
function ReportArtifact({ artifact, markdown }: { artifact: RunArtifact; markdown: string }) {
  const filenameBase = slugify(artifact.title);
  return (
    <Stack gap="3" data-testid="artifact" data-kind={artifact.kind}>
      <Cluster justify="between" gap="3">
        <Cluster gap="2">
          <StatusBadge tone="neutral" size="sm" label={ARTIFACT_KIND_LABEL[artifact.kind]} />
          <Text weight="semibold">{artifact.title}</Text>
        </Cluster>
        <Cluster gap="2">
          <Button
            size="sm"
            variant="secondary"
            iconStart={<Icon as={Icons.Download} size="sm" />}
            onClick={() => downloadTextFile(markdown, `${filenameBase}.md`)}
            data-testid="download-md"
          >
            Markdown
          </Button>
          <Button
            size="sm"
            variant="secondary"
            iconStart={<Icon as={Icons.FileText} size="sm" />}
            onClick={() => printReportAsPdf(markdown, artifact.title)}
            data-testid="download-pdf"
          >
            PDF
          </Button>
        </Cluster>
      </Cluster>
      <Markdown text={markdown} />
      {artifact.sources && artifact.sources.length > 0 && <Sources sources={artifact.sources} />}
    </Stack>
  );
}

/** Hosted-research citations for an artifact (§13) — the run-view source display. */
function Sources({ sources }: { sources: NonNullable<RunArtifact['sources']> }) {
  return (
    <Stack gap="1" data-testid="artifact-sources">
      <Text size="xs" tone="muted" weight="medium">
        Sources
      </Text>
      <ol style={{ margin: 0, paddingLeft: '1.25rem', display: 'grid', gap: '0.25rem' }}>
        {sources.map((source, i) => (
          <li key={`${source.url}-${i}`} data-testid="artifact-source">
            <Link href={source.url} external>
              {source.title || source.url}
            </Link>
            <Text as="span" size="xs" tone="muted" style={{ marginLeft: '0.5rem' }}>
              (accessed {new Date(source.accessedAt).toLocaleDateString()})
            </Text>
          </li>
        ))}
      </ol>
    </Stack>
  );
}
